import React, { useState } from 'react';
import { FaUserPlus, FaEdit, FaTrash, FaSearch, FaShieldAlt, FaUser, FaTimes, FaSave } from 'react-icons/fa';
import { useModal } from '../components/Modal';
import { useToast } from '../components/Toast';
import '../styles/Users.css';

const initialUsers = [
  { id: 1, name: 'Carlos Hew',   email: 'carlos@uas.sr',   phone: '+597 721-1001', role: 'fueler',   status: 'active',   joined: '2025-01-10' },
  { id: 2, name: 'Mark Lie',     email: 'mark@uas.sr',     phone: '+597 721-1002', role: 'fueler',   status: 'active',   joined: '2025-02-14' },
  { id: 3, name: 'Sam Jones',    email: 'sam@uas.sr',      phone: '+597 721-1003', role: 'fueler',   status: 'active',   joined: '2025-03-01' },
  { id: 4, name: 'Lisa Pinas',   email: 'lisa@uas.sr',     phone: '+597 721-1004', role: 'fueler',   status: 'inactive', joined: '2025-04-20' },
  { id: 5, name: 'Admin User',   email: 'admin@uas.sr',    phone: '+597 721-0001', role: 'admin',    status: 'active',   joined: '2024-11-01' },
  { id: 6, name: 'Roy Brunings', email: 'roy@uas.sr',      phone: '+597 721-1005', role: 'fueler',   status: 'active',   joined: '2025-06-15' },
];

function UserForm({ user, onSave, closeModal }) {
  const [form, setForm] = useState(user || { name: '', email: '', phone: '', role: 'fueler', status: 'active' });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...form, id: user?.id || Date.now() });
    closeModal();
  };

  return (
    <form onSubmit={handleSubmit} className="user-form">
      <div className="form-row">
        <label className="form-label">Full name</label>
        <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Carlos Hew" />
      </div>
      <div className="form-row">
        <label className="form-label">Email</label>
        <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} required placeholder="e.g. carlos@uas.sr" />
      </div>
      <div className="form-row">
        <label className="form-label">Phone</label>
        <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+597 ..." />
      </div>
      <div className="form-row-2">
        <div className="form-row">
          <label className="form-label">Role</label>
          <select className="form-input" value={form.role} onChange={e => set('role', e.target.value)}>
            <option value="fueler">Fueler</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Status</label>
          <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      {!user && (
        <div className="form-row">
          <label className="form-label">Password</label>
          <input className="form-input" type="password" required placeholder="Set initial password" />
        </div>
      )}
      <div className="modal-actions">
        <button type="submit" className="btn btn-primary"><FaSave /> {user ? 'Save changes' : 'Create user'}</button>
        <button type="button" className="btn btn-secondary" onClick={closeModal}><FaTimes /> Cancel</button>
      </div>
    </form>
  );
}

function DeleteConfirm({ user, onDelete, closeModal }) {
  return (
    <div className="delete-confirmation">
      <p>Are you sure you want to delete <strong>{user.name}</strong>?</p>
      <p className="warning-text">This action cannot be undone.</p>
      <div className="modal-actions">
        <button className="btn btn-danger" onClick={() => { onDelete(user.id); closeModal(); }}>Delete</button>
        <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
      </div>
    </div>
  );
}

export default function Users() {
  const [users, setUsers] = useState(initialUsers);
  const [search, setSearch] = useState('');
  const { openModal } = useModal();
  const { success } = useToast();

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = (user) => {
    setUsers(prev => {
      const exists = prev.find(u => u.id === user.id);
      if (exists) return prev.map(u => u.id === user.id ? user : u);
      return [...prev, { ...user, joined: new Date().toISOString().split('T')[0] }];
    });
    success(`User ${user.name} saved successfully`);
  };

  const handleDelete = (id) => {
    setUsers(prev => prev.filter(u => u.id !== id));
    success('User deleted');
  };

  const openCreate = () => openModal('Create user', <UserForm onSave={handleSave} />, 'sm');
  const openEdit = (user) => openModal(`Edit — ${user.name}`, <UserForm user={user} onSave={handleSave} />, 'sm');
  const openDelete = (user) => openModal('Delete user', <DeleteConfirm user={user} onDelete={handleDelete} />, 'sm');

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Users</h1>
          <p className="page-subtitle">Manage fueler accounts and admin access</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          <FaUserPlus /> Create user
        </button>
      </div>

      <div className="search-bar">
        <FaSearch className="search-icon" />
        <input
          type="text"
          placeholder="Search by name or email..."
          className="search-input"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="users-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id}>
                <td>
                  <div className="user-cell">
                    <div className="user-avatar-sm">{u.name.charAt(0)}</div>
                    {u.name}
                  </div>
                </td>
                <td>{u.email}</td>
                <td>{u.phone}</td>
                <td>
                  <span className={`role-badge ${u.role}`}>
                    {u.role === 'admin' ? <FaShieldAlt /> : <FaUser />} {u.role}
                  </span>
                </td>
                <td><span className={`status-dot ${u.status}`}>{u.status}</span></td>
                <td>{u.joined}</td>
                <td>
                  <div className="table-actions">
                    <button className="btn btn-sm btn-secondary" onClick={() => openEdit(u)}><FaEdit /></button>
                    <button className="btn btn-sm btn-danger" onClick={() => openDelete(u)}><FaTrash /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="table-empty">No users found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
